"use client"

import Image from "next/image"
import type { components } from "umbraco"
import { getMediaUrl, resolveLinkHref } from "umbraco"
import { Link, getNonUmbracoUrl } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import Container from "../../Container"
import {
  Root,
  Strip,
  Overlay,
  Content,
  TextGroup,
  TitleText,
  SubtitleText,
  CtaLink,
  BackgroundVideo,
  DesktopLayer,
  MobileLayer,
} from "./styles"

const coverStyle = { objectFit: "cover" } as const

// ---------------------------------------------------------------------------
// Rich text link rewriter — resolves raw Umbraco paths in markup
// ---------------------------------------------------------------------------
// EGDE-726: the title renders inside a real <h1> (see TitleText), and an h1
// may only contain phrasing content — the RTE wraps every line in <p> (or a
// pasted heading), so those become <span>s styled `display: block`.
// QA return 2026-08-24: inspecting the title landed on that wrapper <span>
// and read "Not H1" — the h1 was its direct parent. A title that is ONE block
// therefore unwraps entirely (the h1 needs no line breaks for a single line),
// so the element the text lives in IS the h1; only multi-line titles keep the
// spans.
function flattenToSpans(html: string): string {
  // The RTE pads titles with empty paragraphs (the live /business title ships
  // a trailing <p></p>) — they render nothing, so they must not count as a
  // second line when deciding whether the title is single-block. Only the
  // EDGES are stripped: an empty block BETWEEN two text lines is an authored
  // spacer and must survive.
  const trimmed = html
    .replace(
      /^(?:\s*<(p|h[1-6])(?:\s[^>]*)?>(?:\s|&nbsp;|<br\s*\/?>)*<\/\1>)+/i,
      "",
    )
    .replace(
      /(?:<(p|h[1-6])(?:\s[^>]*)?>(?:\s|&nbsp;|<br\s*\/?>)*<\/\1>\s*)+$/i,
      "",
    )
    .trim()
  const single = trimmed.match(
    /^<(?:p|h[1-6])(?:\s[^>]*)?>([\s\S]*)<\/(?:p|h[1-6])>$/i,
  )
  if (single && !/<(?:p|h[1-6])[\s>]/i.test(single[1])) return single[1]
  return trimmed
    .replace(/<(?:p|h[1-6])(\s[^>]*)?>/gi, "<span>")
    .replace(/<\/(?:p|h[1-6])>/gi, "</span>")
}

// EGDE-726: only the title may be an H1 — a pasted h1 in the subtitle would
// hand the page a second one.
function demoteH1(html: string): string {
  return html.replace(/<h1(\s[^>]*)?>/gi, "<h2$1>").replace(/<\/h1>/gi, "</h2>")
}

function processRichTextLinks(html: string): string {
  return html.replace(
    /href="(\/[^"]*?)"/g,
    (match, url: string) => {
      if (url.startsWith("/media/")) {
        return `href="${getUmbracoMediaUrl(url)}"`
      }
      if (url.startsWith("/pages/") || url.startsWith("/")) {
        return `href="${getNonUmbracoUrl(url)}"`
      }
      return match
    },
  )
}

// ---------------------------------------------------------------------------
// Media helpers (image vs video detection)
// ---------------------------------------------------------------------------
function isVideoMedia(mediaPicker: unknown): boolean {
  if (!Array.isArray(mediaPicker) || mediaPicker.length === 0) return false
  const media = mediaPicker[0] as any
  const mediaType = (media?.mediaType ?? "").toLowerCase()
  const extension = (media?.extension ?? "").toLowerCase()
  // EGDE-566/571: Umbraco reports "umbracoMediaVideo" (with empty extension)
  // for uploaded videos — strict equality never matched (cf. EGDE-36).
  return (
    mediaType.includes("video") ||
    ["mp4", "webm", "ogg", "mov"].includes(extension)
  )
}

type InnerHeroData =
  components["schemas"]["InnerHeroBlockPropertiesModel"]

interface Props {
  data: InnerHeroData
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  [key: string]: unknown
}

export default function InnerHeroBlock({ data }: Props) {
  // Resolve background media from block list (image or video)
  const imageBlock = data.backgroundImage?.items?.[0]
  const imageProps = imageBlock?.content?.properties as
    | Record<string, unknown>
    | undefined
  const desktopRel = imageProps?.desktopImage
    ? getMediaUrl(imageProps.desktopImage)
    : null
  const imageUrl = desktopRel ? getUmbracoMediaUrl(desktopRel) : null
  // EGDE-599: the block read only `desktopImage`, so an authored Mobile Image
  // never rendered — mobile showed the desktop asset. Falls back to the desktop
  // asset when no mobile crop is set, per the CMS field description.
  const mobileRel = imageProps?.mobileImage
    ? getMediaUrl(imageProps.mobileImage)
    : null
  const mobileUrl = mobileRel ? getUmbracoMediaUrl(mobileRel) : imageUrl
  const isDesktopVideo = isVideoMedia(imageProps?.desktopImage)
  // EGDE-566 (r2): an authored Mobile Image can be a video too, and when there
  // is none the desktop asset carries over — so mobile must follow whichever
  // asset it actually shows rather than assume it is an image.
  const isMobileVideo = mobileRel
    ? isVideoMedia(imageProps?.mobileImage)
    : isDesktopVideo
  const hasMedia = Boolean(imageUrl)

  // Resolve CTA link — handle Content, Media, and External types
  const ctaResolved = resolveLinkHref(data.ctaLink, {
    getNonUmbracoUrl,
    getUmbracoMediaUrl,
  })
  const href = ctaResolved?.href ?? null
  const ctaLinkType = ctaResolved?.isInternal ? "Content" : undefined
  const ctaTarget = ctaResolved?.target

  return (
    <Root data-block="inner-hero-block">
      <Container paddingMobile>
        <Strip $hasImage={hasMedia}>
          {hasMedia && (
            <>
              <DesktopLayer>
                {isDesktopVideo ? (
                  <BackgroundVideo
                    src={imageUrl!}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                  />
                ) : (
                  <Image
                    src={imageUrl!}
                    alt=""
                    fill
                    priority
                    sizes="(min-width: 1280px) 100vw, 1px"
                    style={coverStyle}
                  />
                )}
              </DesktopLayer>
              <MobileLayer>
                {isMobileVideo ? (
                  <BackgroundVideo
                    src={mobileUrl ?? imageUrl!}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                  />
                ) : (
                  <Image
                    src={mobileUrl ?? imageUrl!}
                    alt=""
                    fill
                    priority
                    sizes="(max-width: 1279px) 100vw, 1px"
                    style={coverStyle}
                  />
                )}
              </MobileLayer>
              <Overlay />
            </>
          )}

          <Content>
            <TextGroup>
              {data.title?.markup && (
                <TitleText
                  $hasImage={hasMedia}
                  dangerouslySetInnerHTML={{
                    __html: flattenToSpans(
                      processRichTextLinks(data.title.markup),
                    ),
                  }}
                />
              )}
              {data.subtitle?.markup && (
                <SubtitleText
                  $hasImage={hasMedia}
                  dangerouslySetInnerHTML={{
                    __html: demoteH1(
                      processRichTextLinks(data.subtitle.markup),
                    ),
                  }}
                />
              )}
            </TextGroup>

            {data.ctaLabel && href && (
              <CtaLink
                {...(ctaLinkType === "Content" ? { as: Link } : {})}
                href={href}
                target={ctaTarget}
                rel={
                  ctaTarget === "_blank"
                    ? "noopener noreferrer"
                    : undefined
                }
              >
                {data.ctaLabel}
              </CtaLink>
            )}
          </Content>
        </Strip>
      </Container>
    </Root>
  )
}
